import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { Chess, type Color, type PieceSymbol, type Square } from "chess.js";
import type { Side } from "../../shared/game.ts";

const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;
const RANKS = ["1", "2", "3", "4", "5", "6", "7", "8"] as const;

const PIECES: Record<Color, Record<PieceSymbol, string>> = {
  w: { k: "♚︎", q: "♛︎", r: "♜︎", b: "♝︎", n: "♞︎", p: "♟︎" },
  b: { k: "♚︎", q: "♛︎", r: "♜︎", b: "♝︎", n: "♞︎", p: "♟︎" },
};

const PIECE_NAMES: Record<PieceSymbol, string> = {
  k: "король",
  q: "ферзь",
  r: "ладья",
  b: "слон",
  n: "конь",
  p: "пешка",
};

function displayedSquares(orientation: Side): Square[] {
  const ranks = orientation === "white" ? [...RANKS].reverse() : [...RANKS];
  const files = orientation === "white" ? [...FILES] : [...FILES].reverse();
  return ranks.flatMap((rank) => files.map((file) => `${file}${rank}` as Square));
}

function pieceSide(color: Color): Side {
  return color === "w" ? "white" : "black";
}

function isSquare(value: string): value is Square {
  return /^[a-h][1-8]$/.test(value);
}

interface ChessBoardProps {
  fen: string;
  orientation: Side;
  playerSide: Side | null;
  turn: Side;
  active: boolean;
  check: boolean;
  lastMove: { from: string; to: string } | null;
  onMove: (from: string, to: string, promotionOptions: string[]) => void;
}

export function ChessBoard({
  fen,
  orientation,
  playerSide,
  turn,
  active,
  check,
  lastMove,
  onMove,
}: ChessBoardProps) {
  const chess = useMemo(() => new Chess(fen), [fen]);
  const squares = useMemo(() => displayedSquares(orientation), [orientation]);
  const [selected, setSelected] = useState<Square | null>(null);
  const dragSourceRef = useRef<Square | null>(null);

  useEffect(() => {
    setSelected(null);
    dragSourceRef.current = null;
  }, [fen, orientation]);

  const legalMoves = useMemo(() => {
    if (!selected) return [];
    return chess.moves({ square: selected, verbose: true });
  }, [chess, selected]);

  const legalDestinations = new Set(legalMoves.map((move) => move.to));
  const canControl = active && playerSide === turn;
  let checkedKing: Square | null = null;
  if (check) {
    for (const square of squares) {
      const piece = chess.get(square);
      if (piece?.type === "k" && pieceSide(piece.color) === turn) checkedKing = square;
    }
  }

  const canSelect = (square: Square) => {
    const piece = chess.get(square);
    return Boolean(piece && playerSide && pieceSide(piece.color) === playerSide && canControl);
  };

  const chooseDestination = (square: Square) => {
    if (selected && legalDestinations.has(square)) {
      const promotions = legalMoves
        .filter((move) => move.to === square && move.promotion)
        .map((move) => move.promotion)
        .filter((value): value is PieceSymbol => Boolean(value));
      onMove(selected, square, [...new Set(promotions)]);
      if (!promotions.length) setSelected(null);
      return;
    }
    if (canSelect(square)) setSelected(square);
    else setSelected(null);
  };

  const onDragStart = (event: DragEvent, square: Square) => {
    if (!canSelect(square)) return event.preventDefault();
    dragSourceRef.current = square;
    setSelected(square);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", square);
  };

  const onDrop = (event: DragEvent, square: Square) => {
    event.preventDefault();
    const transferred = event.dataTransfer.getData("text/plain");
    const from = isSquare(transferred) ? transferred : null;
    const source = from ?? dragSourceRef.current ?? selected;
    dragSourceRef.current = null;
    if (!source) return;
    const moves = chess.moves({ square: source, verbose: true }).filter((move) => move.to === square);
    if (!moves.length) return setSelected(null);
    const promotions = moves.map((move) => move.promotion).filter((value): value is PieceSymbol => Boolean(value));
    onMove(source, square, [...new Set(promotions)]);
    if (!promotions.length) setSelected(null);
  };

  return (
    <div className="board-shell">
      <div className="chess-board" role="grid" aria-label={`Шахматная доска, ходят ${turn === "white" ? "белые" : "чёрные"}`}>
        {squares.map((square, index) => {
          const piece = chess.get(square);
          const fileIndex = FILES.indexOf(square[0] as (typeof FILES)[number]);
          const rankIndex = Number(square[1]) - 1;
          const light = (fileIndex + rankIndex) % 2 === 1;
          const legal = legalDestinations.has(square);
          const capture = legal && Boolean(piece);
          const isLastMove = lastMove?.from === square || lastMove?.to === square;
          const label = piece
            ? `${square}: ${pieceSide(piece.color) === "white" ? "белые" : "чёрные"}, ${PIECE_NAMES[piece.type]}`
            : `${square}: пустая клетка`;
          return (
            <button
              type="button"
              role="gridcell"
              key={square}
              className={[
                "board-square",
                light ? "square-light" : "square-dark",
                selected === square ? "square-selected" : "",
                isLastMove ? "square-last-move" : "",
                checkedKing === square ? "square-check" : "",
                legal ? (capture ? "square-legal-capture" : "square-legal") : "",
              ].filter(Boolean).join(" ")}
              aria-label={label}
              aria-pressed={selected === square}
              data-square={square}
              onClick={() => chooseDestination(square)}
              onDragOver={(event) => {
                if (!canControl) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
              }}
              onDrop={(event) => onDrop(event, square)}
            >
              {index >= 56 && <span className="coordinate coordinate-file">{square[0]}</span>}
              {index % 8 === 0 && <span className="coordinate coordinate-rank">{square[1]}</span>}
              {piece && (
                <span
                  className={`chess-piece piece-${pieceSide(piece.color)}`}
                  draggable={canSelect(square)}
                  onDragStart={(event) => onDragStart(event, square)}
                  onDragEnd={() => {
                    dragSourceRef.current = null;
                  }}
                  aria-hidden="true"
                >
                  {PIECES[piece.color][piece.type]}
                </span>
              )}
              {legal && <span className="legal-marker" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PromotionPicker({
  side,
  onChoose,
  onCancel,
}: {
  side: Side;
  onChoose: (piece: string) => void;
  onCancel: () => void;
}) {
  const color: Color = side === "white" ? "w" : "b";
  const choices: PieceSymbol[] = ["q", "r", "b", "n"];
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onCancel()}>
      <section className="modal-card promotion-card" role="dialog" aria-modal="true" aria-labelledby="promotion-title">
        <div className="modal-heading">
          <div>
            <span className="eyebrow">Последняя горизонталь</span>
            <h2 id="promotion-title">Выберите фигуру</h2>
          </div>
        </div>
        <div className="promotion-options">
          {choices.map((piece) => (
            <button key={piece} type="button" onClick={() => onChoose(piece)} aria-label={`Превратить в ${PIECE_NAMES[piece]}`}>
              <span className={`chess-piece piece-${side}`}>{PIECES[color][piece]}</span>
              <small>{PIECE_NAMES[piece]}</small>
            </button>
          ))}
        </div>
        <button className="button button-quiet button-full" type="button" onClick={onCancel}>Отмена</button>
      </section>
    </div>
  );
}
